import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import type { QuotaPressure, QuotaPressureLevel } from "@/lib/quota/deriveQuotaPressure";

interface QuotaWarningProps {
  /** Legacy clip-exhaustion trigger; still authoritative for paid plans with a finite cap. */
  hasClipQuota: boolean;
  /** Derived pressure, which also carries the earlier "approaching" state. */
  pressure?: QuotaPressure | null;
}

const QuotaWarning = ({ hasClipQuota, pressure = null }: QuotaWarningProps) => {
  const { t } = useTranslation();

  // Exhaustion wins: `hasClipQuota` still fires for a paid plan with a finite
  // clip cap, where `deriveQuotaPressure` deliberately stays silent.
  const level: QuotaPressureLevel | null = !hasClipQuota
    ? "exhausted"
    : pressure?.level ?? null;

  if (!level) return null;

  const isApproaching = level === "approaching";
  const Icon = isApproaching ? Clock : AlertTriangle;

  const message = isApproaching && pressure
    ? t(`quotaWarning.approaching.${pressure.metric}`, {
        remaining: pressure.remaining,
        percentage: pressure.percentage,
      })
    : t("quotaWarning.limitReached");

  const tone = isApproaching
    ? {
        container: "bg-amber-50 border-amber-200 text-amber-800",
        icon: "text-amber-600",
        link: "text-amber-700 hover:text-amber-900",
      }
    : {
        container: "bg-orange-50 border-orange-200 text-orange-800",
        icon: "text-orange-600",
        link: "text-orange-700 hover:text-orange-900",
      };

  return (
    <div
      data-testid="quota-warning"
      data-level={level}
      className={`flex items-center gap-2 border rounded-lg p-2 text-sm ${tone.container}`}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${tone.icon}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs leading-tight">{message}</p>
        <Button
          asChild
          variant="link"
          size="sm"
          className={`h-auto p-0 text-xs underline ${tone.link}`}
        >
          <Link to="/plans">
            {t('quotaWarning.viewPlans')}
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default QuotaWarning;
